import { useContext, useEffect, useRef, useState } from "react";

import InputBase from "@material-ui/core/InputBase";
import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import TicketsList from "../TicketsListCustom";

import {
  Button,
  Chip,
  Drawer,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Hidden,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  useMediaQuery,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import DateRangeIcon from '@material-ui/icons/DateRange';
import MenuIcon from '@material-ui/icons/Menu';
import ViewComfyIcon from '@material-ui/icons/ViewComfy';
import ViewListIcon from '@material-ui/icons/ViewList';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import useSettings from "../../hooks/useSettings";
import api from "../../services/api";
import { checkRole } from "../../helpers/checkRole";
import { i18n } from "../../translate/i18n";
import { ConnectionsFilter } from "../ConnectionsFilter";
import { ContactsFilter } from "../ContactsFilter";
import { TablePaginationFooter } from "../TablePaginationFooter";
import { TagsFilter } from "../TagsFilter";
import TicketsQueueSelect from "../TicketsQueueSelect";
import { UsersFilter } from "../UsersFilter";

const REPORT_VIEW_PREFERENCES_KEY = "report-ticket-view-preferences";

const PERIOD_OPTIONS = [
  { value: "today", label: "Hoje" },
  { value: "month", label: "Este mês" },
  { value: "year", label: "Este ano" },
  { value: "all", label: "Desde o início" },
  { value: "custom", label: "Personalizado" },
];

const formatIsoDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

// Retorna o intervalo de abertura de cada opção predefinida (vazio = sem filtro).
const getPresetRange = (preset) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  switch (preset) {
    case "today":
      return { from: formatIsoDate(now), to: formatIsoDate(now) };
    case "month":
      return {
        from: formatIsoDate(new Date(year, month, 1)),
        to: formatIsoDate(new Date(year, month + 1, 0)),
      };
    case "year":
      return {
        from: formatIsoDate(new Date(year, 0, 1)),
        to: formatIsoDate(new Date(year, 11, 31)),
      };
    default:
      return { from: "", to: "" };
  }
};

const getReportViewPreferences = () => {
  const defaults = { itemSize: "large", columns: 1 };

  try {
    const savedPreferences = JSON.parse(
      window.localStorage.getItem(REPORT_VIEW_PREFERENCES_KEY)
    );

    return {
      itemSize: ["small", "medium", "large"].includes(savedPreferences?.itemSize)
        ? savedPreferences.itemSize
        : defaults.itemSize,
      columns: [1, 2, 3].includes(savedPreferences?.columns)
        ? savedPreferences.columns
        : defaults.columns,
    };
  } catch {
    return defaults;
  }
};

const useStyles = makeStyles((theme) => ({
  reportFilterControl: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%",
    "& .MuiOutlinedInput-root": {
      height: 40,
      backgroundColor:
        theme.palette.type === "dark"
          ? theme.palette.dark_400.main
          : theme.palette.gray_50.main,
      borderRadius: 8,
    },
    "& .MuiSelect-outlined": {
      paddingTop: 10,
      paddingBottom: 10,
    },
    "& .MuiFormLabel-root": {
      color:
        theme.palette.type === "dark"
          ? theme.palette.dark_50.main
          : theme.palette.gray_400.main,
    },
  },
  selectRenderValue: {
    display: "block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  periodFilterButton: {
    minHeight: 40,
    justifyContent: "flex-start",
    borderRadius: 8,
  },
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    padding: theme.spacing(2),
    // Reserva espaço para não ficar sob a barra fixa de chat interno do lado direito.
    paddingRight: `calc(${theme.spacing(2)}px + 56px)`,
    [theme.breakpoints.up("lg")]: {
      paddingRight: `calc(${theme.spacing(2)}px + 64px)`,
    },
  },

  filtersHeader: {
    flex: "none",
  },
  filtersRow: {
    display: "flex",
    flexWrap: "nowrap",
    gap: theme.spacing(1),
    overflowX: "hidden",
    paddingBottom: theme.spacing(1),
  },
  filterItem: {
    flex: "1 1 0",
    minWidth: 0,
    maxWidth: "100%",
  },
  filtersRowMobile: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    minWidth: 280,
    maxWidth: "80vw",
  },
  filterItemMobile: {
    width: "100%",
  },
  mobileFilterButton: {
    alignSelf: "flex-start",
    marginBottom: theme.spacing(1),
  },

  activeFilters: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },

  ticketsListArea: {
    flex: 1,
    minHeight: 0,
    display: "flex",
  },
  listActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(1),
  },

  tabsHeader: {
    flex: "none",
    // backgroundColor: "#eee",
  },

  settingsIcon: {
    alignSelf: "center",
    marginLeft: "auto",
    padding: 8,
  },
  itemSizeButton: {
    alignSelf: "center",
    marginLeft: "auto",
  },

  tabWithGroups: {
    minWidth: 90,
    width: 90,
  },

  tab: {
    minWidth: 120,
    width: 120,
  },

  ticketOptionsBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    // background: "#fafafa",
    padding: theme.spacing(1),
  },

  serachInputWrapper: {
    flex: 1,
    // background: "#fff",
    display: "flex",
    borderRadius: 40,
    padding: 4,
    marginRight: theme.spacing(1),
  },

  searchIcon: {
    color: "grey",
    marginLeft: 6,
    marginRight: 6,
    alignSelf: "center",
  },

  searchInput: {
    flex: 1,
    border: "none",
    borderRadius: 30,
  },

  badge: {
    right: "-10px",
  },
  show: {
    display: "block",
  },
  hide: {
    display: "none !important",
  },

  icon24: {
    width: 24,
    height: 24,
  },
}));


const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'open', label: 'Ativos' },
  { value: 'closed', label: 'Fechado' },
  { value: 'pending', label: 'Pendente' }
];

const StatusSelect = ({ handleSelectedStatus }) => {
  const classes = useStyles();
  const [status, setStatus] = useState('');

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
    handleSelectedStatus(event.target.value);
  };

  const selectedLabel = STATUS_OPTIONS.find(option => option.value === status)?.label || 'Todos';

  return (
    <FormControl className={classes.reportFilterControl}>
      <FormLabel id="status-select-label">Status</FormLabel>
      <Select
        labelId="status-select-label"
        id="status-select"
        displayEmpty
        variant="outlined"
        value={status}
        onChange={handleStatusChange}
        renderValue={() => (
          <span className={classes.selectRenderValue} title={selectedLabel}>
            {selectedLabel}
          </span>
        )}
      >
        {STATUS_OPTIONS.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const TicketListItemCustomRelatorio = () => {
  const classes = useStyles();
  // const history = useHistory();

  const [searchParam, setSearchParam] = useState("");
  const [tab] = useState("search");
  // const [, setTabOpen] = useState("search");
  // const [, setNewTicketModalOpen] = useState(false);
  const [, setShowAllTickets] = useState(false);
  const searchInputRef = useRef();
  const { user } = useContext(AuthContext);
  // const { profile } = user;

  // const [openCount, setOpenCount] = useState(0);
  // const [pendingCount, setPendingCount] = useState(0);

  const userQueueIds = user.queues && Array.isArray(user.queues) ? user.queues.map((q) => q.id) : [];
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedConnections, setSelectedConnections] = useState([]);
  const [selectedConnectionObjects, setSelectedConnectionObjects] = useState([]);
  const [allowedQueueIds, setAllowedQueueIds] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showChatbots, setShowChatbots] = useState(false);
  const [filterGroups, setFilterGroups] = useState("");
  const [sortConfig, setSortConfig] = useState({ column: '', order: '' });
  const [reportViewPreferences] = useState(getReportViewPreferences);
  const [ticketItemSize, setTicketItemSize] = useState(reportViewPreferences.itemSize);
  const [ticketColumns, setTicketColumns] = useState(reportViewPreferences.columns);
  const [itemSizeMenuAnchor, setItemSizeMenuAnchor] = useState(null);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [noQueueSelected, setNoQueueSelected] = useState(false);
  const [periodAnchor, setPeriodAnchor] = useState(null);
  const [periodOption, setPeriodOption] = useState("all");
  const [draftPeriod, setDraftPeriod] = useState("all");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [openingDateFrom, setOpeningDateFrom] = useState("");
  const [openingDateTo, setOpeningDateTo] = useState("");
  const [closingDateFrom, setClosingDateFrom] = useState("");
  const [closingDateTo, setClosingDateTo] = useState("");
  const [draftDates, setDraftDates] = useState({
    openingDateFrom: "",
    openingDateTo: "",
    closingDateFrom: "",
    closingDateTo: "",
  });

  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalTickets, setTotalTickets] = useState(0);
  const [ticketsLoading, setTicketsLoading] = useState(false);

  const { getSetting } = useSettings();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("sm"));
  const [showTabGroups, setShowTabGroups] = useState(false);


  useEffect(() => {
    Promise.all([
      getSetting("CheckMsgIsGroup"),
      getSetting("groupsTab")
    ]).then(([ignoreGroups, groupsTab]) => {
      setShowTabGroups(ignoreGroups === "disabled" && groupsTab === "enabled");
    });
  }, []);

  useEffect(() => {
    if (
      user.profile.toUpperCase() === "ADMIN" ||
      checkRole(user, "super") ||
      checkRole(user, "gestor")
    ) {
      setShowAllTickets(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    /*  if (tab === "search") {
       searchInputRef.current.focus();
     } */
  }, [tab]);

  useEffect(() => {
    window.localStorage.setItem(
      REPORT_VIEW_PREFERENCES_KEY,
      JSON.stringify({ itemSize: ticketItemSize, columns: ticketColumns })
    );
  }, [ticketColumns, ticketItemSize]);

  let searchTimeout;

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();

    clearTimeout(searchTimeout);

    searchTimeout = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, 500);
  };

  // const handleChangeTab = (e, newValue) => {
  //   setTab(newValue);
  // };

  // const handleChangeTabOpen = (e, newValue) => {
  //   setTabOpen(newValue);
  // };



  // const handleCloseOrOpenTicket = (ticket) => {
  //   setNewTicketModalOpen(false);
  //   if (ticket !== undefined && ticket.uuid !== undefined) {
  //     history.push(`/tickets/${ticket.uuid}`);
  //   }
  // };

  const handleSelectedTags = (selecteds) => {
    const tags = selecteds.map((t) => t.id);
    setSelectedTags(tags);
  };

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);
    setSelectedUsers(users);
  };

  const handleSelectedConnections = (selecteds, meta = {}) => {
    setSelectedConnectionObjects(selecteds);
    if (meta.isAllSelected || selecteds.length === 0) {
      setSelectedConnections([]);
      return;
    }
    const connections = selecteds.map((t) => t.id);
    setSelectedConnections(connections);
  };

  useEffect(() => {
    const botQueueIds = Array.from(
      new Set(
        selectedConnectionObjects
          .flatMap((conn) => conn.queues || [])
          .filter((q) => q.isBot)
          .map((q) => Number(q.id))
      )
    );

    if (botQueueIds.length === 0) {
      setAllowedQueueIds(null);
      setSelectedQueueIds([]);
      return undefined;
    }

    let isMounted = true;
    const fetchAccessibleDepartments = async () => {
      try {
        const { data } = await api.get(
          `/queue/accessible-by-bots?queueIds=${botQueueIds.join(",")}`
        );
        if (isMounted) {
          const queueIds = (data || []).map((d) => Number(d.id));
          setAllowedQueueIds(queueIds);
          setSelectedQueueIds(queueIds);
        }
      } catch (err) {
        if (isMounted) {
          toastError(err);
          setAllowedQueueIds([]);
          setSelectedQueueIds([]);
        }
      }
    };

    fetchAccessibleDepartments();
    return () => {
      isMounted = false;
    };
  }, [selectedConnectionObjects]);

  const handleSelectedStatus = (status) => {
    setShowChatbots(status === "chatbot");
    setSelectedStatus(status === "chatbot" ? "" : status);
  };

  const handleSelectedContact = (contacts) => {
    setSelectedContactIds(contacts ? contacts.map(c => c.id) : []);
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value);
    setPageNumber(1);
  };

  const handleItemSizeChange = (size) => {
    setTicketItemSize(size);
    setItemSizeMenuAnchor(null);
  };

  const handleTicketColumnsChange = (columns) => {
    setTicketColumns(columns);
    setItemSizeMenuAnchor(null);
  };

  const openDateModal = (event) => {
    setDraftDates({ openingDateFrom, openingDateTo, closingDateFrom, closingDateTo });
    setDraftPeriod(periodOption);
    setPeriodAnchor(event.currentTarget);
  };

  const closeDateModal = () => setPeriodAnchor(null);

  const applyDateFilters = () => {
    if (draftPeriod === "custom") {
      setOpeningDateFrom(draftDates.openingDateFrom);
      setOpeningDateTo(draftDates.openingDateTo);
      setClosingDateFrom(draftDates.closingDateFrom);
      setClosingDateTo(draftDates.closingDateTo);
    } else {
      const range = getPresetRange(draftPeriod);
      setOpeningDateFrom(range.from);
      setOpeningDateTo(range.to);
      setClosingDateFrom("");
      setClosingDateTo("");
    }
    setPeriodOption(draftPeriod);
    closeDateModal();
  };

  const clearDateFilters = () => {
    setPeriodOption("all");
    setDraftPeriod("all");
    closeDateModal();
    const emptyDates = {
      openingDateFrom: "",
      openingDateTo: "",
      closingDateFrom: "",
      closingDateTo: "",
    };
    setDraftDates(emptyDates);
    setOpeningDateFrom("");
    setOpeningDateTo("");
    setClosingDateFrom("");
    setClosingDateTo("");
  };

  const formatDateRange = (label, from, to) => {
    if (!from && !to) return null;
    const formattedFrom = from ? from.split("-").reverse().join("/") : "início";
    const formattedTo = to ? to.split("-").reverse().join("/") : "hoje";
    return `${label}: ${formattedFrom} a ${formattedTo}`;
  };

  const renderFilters = (mobile = false) => {
    const rowClass = mobile ? classes.filtersRowMobile : classes.filtersRow;
    const itemClass = mobile ? classes.filterItemMobile : classes.filterItem;

    return (
      <div className={rowClass}>
        <div className={itemClass}>
          <ContactsFilter onFiltered={handleSelectedContact} />
        </div>
        <div className={itemClass}>
          <ConnectionsFilter onFiltered={handleSelectedConnections} />
        </div>
        <div className={itemClass}>
          <TagsFilter onFiltered={handleSelectedTags} />
        </div>
        <div className={itemClass}>
          <TicketsQueueSelect
            className={classes.reportFilterControl}
            label="Departamento"
            selectedQueueIds={selectedQueueIds}
            userQueues={user?.queues}
            onChange={(values) => setSelectedQueueIds(values)}
            loadSettingsQueues={true}
            onlyUserDepartments={true}
            selectAllByDefault={true}
            includeNoQueueOption={true}
            noQueueSelected={noQueueSelected}
            onNoQueueChange={setNoQueueSelected}
            allowedQueueIds={allowedQueueIds}
          />
        </div>
        <div className={itemClass}>
          <UsersFilter onFiltered={handleSelectedUsers} />
        </div>
        <div className={itemClass}>
          <StatusSelect handleSelectedStatus={handleSelectedStatus} />
        </div>
        <div className={itemClass}>
          <FormControl className={classes.reportFilterControl}>
            <FormLabel id="groups-label">Grupos</FormLabel>
            <Select
              labelId="groups-label"
              displayEmpty
              variant="outlined"
              value={filterGroups}
              onChange={(e) => setFilterGroups(e.target.value)}
              renderValue={() => {
                const groupLabel =
                  filterGroups === "true"
                    ? "Apenas Grupos"
                    : filterGroups === "false"
                      ? "Apenas Contatos"
                      : "Ambos";
                return (
                  <span className={classes.selectRenderValue} title={groupLabel}>
                    {groupLabel}
                  </span>
                );
              }}
            >
              <MenuItem value="">Ambos</MenuItem>
              <MenuItem value="true">Apenas Grupos</MenuItem>
              <MenuItem value="false">Apenas Contatos</MenuItem>
            </Select>
          </FormControl>
        </div>
        <div className={itemClass}>
          <FormControl className={classes.reportFilterControl}>
            <FormLabel>Período</FormLabel>
            <Button
              className={classes.periodFilterButton}
              fullWidth
              variant="outlined"
              startIcon={<DateRangeIcon />}
              onClick={openDateModal}
            >
              {PERIOD_OPTIONS.find((option) => option.value === periodOption)?.label}
            </Button>
          </FormControl>
        </div>
      </div>
    );
  };

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper} style={{ maxWidth: '100%' }}>

      <div className={classes.filtersHeader}>
        {/* <Paper square elevation={0} className={classes.ticketOptionsBox} style={{ padding: 0, margin: 0 }}>

          <div className={classes.serachInputWrapper}>
            <SearchIcon className={classes.searchIcon} />
            <InputBase
              className={classes.searchInput}
              inputRef={searchInputRef}
              placeholder={i18n.t("tickets.search.placeholder")}
              type="search"
              onChange={handleSearch}
            />
          </div>
        </Paper> */}
        <Hidden smDown>
          {renderFilters(false)}
        </Hidden>
        <Hidden mdUp>
          <Button
            variant="outlined"
            startIcon={<MenuIcon />}
            className={classes.mobileFilterButton}
            onClick={() => setMobileFiltersOpen(true)}
          >
            Filtros
          </Button>
          <Drawer
            anchor="right"
            open={mobileFiltersOpen}
            onClose={() => setMobileFiltersOpen(false)}
          >
            {renderFilters(true)}
          </Drawer>
        </Hidden>
        <div className={classes.activeFilters}>
          {formatDateRange("Abertura", openingDateFrom, openingDateTo) && (
            <Chip
              label={formatDateRange("Abertura", openingDateFrom, openingDateTo)}
              onDelete={() => {
                setOpeningDateFrom("");
                setOpeningDateTo("");
                setPeriodOption(closingDateFrom || closingDateTo ? "custom" : "all");
              }}
              deleteIcon={<ClearIcon />}
              size="small"
            />
          )}
          {formatDateRange("Fechamento", closingDateFrom, closingDateTo) && (
            <Chip
              label={formatDateRange("Fechamento", closingDateFrom, closingDateTo)}
              onDelete={() => {
                setClosingDateFrom("");
                setClosingDateTo("");
                setPeriodOption(openingDateFrom || openingDateTo ? "custom" : "all");
              }}
              deleteIcon={<ClearIcon />}
              size="small"
            />
          )}
        </div>
      </div>

      {/* <div className={classes.listActions}>
        <Tooltip title="Exibição dos atendimentos">
          <IconButton
            className={classes.itemSizeButton}
            aria-label="Exibição dos atendimentos"
            onClick={(event) => setItemSizeMenuAnchor(event.currentTarget)}
          >
            <Rows3 size={20} />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={itemSizeMenuAnchor}
          keepMounted
          open={Boolean(itemSizeMenuAnchor)}
          onClose={() => setItemSizeMenuAnchor(null)}
        >
          <MenuItem disabled>Altura dos itens</MenuItem>
          <MenuItem
            selected={ticketItemSize === "small"}
            onClick={() => handleItemSizeChange("small")}
          >
            Pequeno
          </MenuItem>
          <MenuItem
            selected={ticketItemSize === "medium"}
            onClick={() => handleItemSizeChange("medium")}
          >
            Médio
          </MenuItem>
          <MenuItem
            selected={ticketItemSize === "large"}
            onClick={() => handleItemSizeChange("large")}
          >
            Grande
          </MenuItem>
          <MenuItem disabled>Colunas</MenuItem>
          <MenuItem
            selected={ticketColumns === 1}
            onClick={() => handleTicketColumnsChange(1)}
          >
            <ViewListIcon fontSize="small" />
            Uma coluna
          </MenuItem>
          <MenuItem
            selected={ticketColumns === 2}
            onClick={() => handleTicketColumnsChange(2)}
          >
            <ViewModuleIcon fontSize="small" />
            Duas colunas
          </MenuItem>
          <MenuItem
            selected={ticketColumns === 3}
            onClick={() => handleTicketColumnsChange(3)}
          >
            <ViewComfyIcon fontSize="small" />
            Três colunas
          </MenuItem>
        </Menu>
      </div> */}

      <div className={classes.ticketsListArea}>
        <TicketsList
          isSearch={true}
          searchParam={searchParam}
          showAll={true}
          groups={filterGroups}
          chatbots={showChatbots ? "true" : selectedStatus === "" ? "all" : undefined}
          tags={selectedTags}
          users={selectedUsers}
          contactIds={selectedContactIds}
          whatsappIds={selectedConnections}

          dateFrom={openingDateFrom}
          dateTo={openingDateTo}
          closingDateFrom={closingDateFrom}
          closingDateTo={closingDateTo}
          selectedQueueIds={selectedQueueIds}
          noQueue={noQueueSelected}
          itemSize={ticketItemSize}
          columns={ticketColumns}
          sortBy={sortConfig.column}
          sortOrder={sortConfig.order}
          showReportDetails
          showTabGroups={showTabGroups}
          status={selectedStatus}
          limit={rowsPerPage}
          controlledPageNumber={pageNumber}
          onPageNumberChange={setPageNumber}
          disableInfiniteScroll={true}
          updateTotal={setTotalTickets}
          onLoadingChange={setTicketsLoading}
          style={{ flex: 1, minHeight: 0 }}
        />
      </div>

      <TablePaginationFooter
        pageNumber={pageNumber}
        rowsPerPage={rowsPerPage}
        totalItems={totalTickets}
        loading={ticketsLoading}
        onChangePage={setPageNumber}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        title="atendimentos"
      />
      <Popover
        open={Boolean(periodAnchor)}
        anchorEl={periodAnchor}
        onClose={closeDateModal}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div style={{ padding: 16, width: 320 }}>
          <RadioGroup
            value={draftPeriod}
            onChange={(event) => setDraftPeriod(event.target.value)}
          >
            {PERIOD_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio color="primary" size="small" />}
                label={option.label}
              />
            ))}
          </RadioGroup>
          {draftPeriod === "custom" && (
            <Grid container spacing={2} style={{ marginTop: 8 }}>
              <Grid item xs={12}><strong>Data de abertura</strong></Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="De"
                  type="date"
                  value={draftDates.openingDateFrom}
                  onChange={(event) => setDraftDates({ ...draftDates, openingDateFrom: event.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Até"
                  type="date"
                  value={draftDates.openingDateTo}
                  onChange={(event) => setDraftDates({ ...draftDates, openingDateTo: event.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}><strong>Data de fechamento</strong></Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="De"
                  type="date"
                  value={draftDates.closingDateFrom}
                  onChange={(event) => setDraftDates({ ...draftDates, closingDateFrom: event.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Até"
                  type="date"
                  value={draftDates.closingDateTo}
                  onChange={(event) => setDraftDates({ ...draftDates, closingDateTo: event.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
            <Button onClick={clearDateFilters}>Limpar</Button>
            <Button color="primary" variant="contained" onClick={applyDateFilters}>Aplicar</Button>
          </div>
        </div>
      </Popover>
    </Paper>
  );
};

export default TicketListItemCustomRelatorio;